import { useCallback, useEffect, useState } from 'react'
import { Plus, KeyRound, UserCog, Download } from 'lucide-react'
import { supabase } from '../services/supabase/client'
import {
  listClerkAdmins, updateClerkPermissions, manageClerk, exportInvoicesRange,
  CLERK_PERMISSIONS, CLERK_AMOUNT_PERMISSIONS,
  type ClerkAdmin,
} from '../services/data'
import { downloadCsv } from '../services/reports'
import { PageHeader, Card, DataTable, Spinner, Button } from '../components/shared/ui'
import { QuickBooksExport } from '../components/QuickBooksExport'
import { toast } from 'react-toastify'

const todayStr = () => new Date().toISOString().slice(0, 10)
const monthAgoStr = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10) }
const nextDay = (d: string) => { const x = new Date(d); x.setDate(x.getDate() + 1); return x.toISOString().slice(0, 10) }

interface ActivityRow {
  id: string
  form: string | null
  entry_type: string | null
  entry: string | null
  activity_date: string | null
  activity_time: string | null
  computer_name: string | null
  [key: string]: unknown
}

const ROLES = ['cashier', 'manager', 'admin'] as const

export default function AdminDashboard() {
  const [clerks, setClerks] = useState<ClerkAdmin[]>([])
  const [activity, setActivity] = useState<ActivityRow[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<ClerkAdmin | 'new' | null>(null)
  const [expStart, setExpStart] = useState(monthAgoStr())
  const [expEnd, setExpEnd] = useState(todayStr())
  const [exporting, setExporting] = useState(false)
  const [qbExportOpen, setQbExportOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [cl, a] = await Promise.all([
        listClerkAdmins(),
        supabase.from('activity_log').select('*').order('id', { ascending: false }).limit(100),
      ])
      setClerks(cl)
      setActivity((a.data ?? []) as ActivityRow[])
    } catch (e) {
      console.error(e); toast.error('Failed to load administration data')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const toggleActive = async (c: ClerkAdmin) => {
    if (!c.user_id) { toast.error('This clerk has no linked login.'); return }
    try {
      await manageClerk('set_active', { user_id: c.user_id, is_active: !c.is_active })
      toast.success(`${c.username} ${c.is_active ? 'deactivated' : 'activated'}`)
      load()
    } catch (e) { toast.error((e as Error).message) }
  }

  const exportInvoices = async () => {
    setExporting(true)
    try {
      const rows = await exportInvoicesRange(expStart, nextDay(expEnd))
      if (rows.length === 0) { toast.info('No invoices in that date range'); return }
      downloadCsv(`invoices_${expStart}_to_${expEnd}.csv`, rows)
      toast.success(`Exported ${rows.length} invoices`)
    } catch (e) { console.error(e); toast.error('Export failed') }
    finally { setExporting(false) }
  }

  if (loading) return <Spinner label="Loading administration…" />

  return (
    <div>
      <PageHeader title="Administration" subtitle="Clerks, roles, permissions & audit logs"
        actions={<Button onClick={() => setEditing('new')}><Plus size={16} /> New Clerk</Button>} />

      <Card className="p-5 mb-6">
        <h3 className="font-semibold text-gray-800 mb-4">Clerks &amp; Permissions ({clerks.length})</h3>
        <DataTable
          rows={clerks}
          onRowClick={c => setEditing(c)}
          emptyMessage="No clerks configured yet"
          columns={[
            { key: 'username', header: 'Name', render: c => <span className="font-medium text-gray-900">{c.username}</span> },
            { key: 'email', header: 'Login', render: c => <span className="text-gray-500">{c.email}</span> },
            { key: 'role', header: 'Role', render: c => <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs capitalize">{c.role}</span> },
            { key: 'is_active', header: 'Status', render: c => (
              <span className={`px-2 py-0.5 rounded text-xs ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                {c.is_active ? 'Active' : 'Inactive'}
              </span>
            ) },
            { key: 'perms', header: 'Permissions', render: c => `${Object.values(c.perms).filter(v => v === true).length} enabled` },
            { key: 'actions', header: '', render: c => (
              <div className="flex items-center gap-2 justify-end">
                <button onClick={e => { e.stopPropagation(); toggleActive(c) }}
                  className={`text-xs hover:underline ${c.is_active ? 'text-red-600' : 'text-emerald-700'}`}>
                  {c.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={e => { e.stopPropagation(); setEditing(c) }} className="text-gray-400 hover:text-green-700" title="Edit"><UserCog size={16} /></button>
              </div>
            ) },
          ]}
        />
      </Card>

      <Card className="p-5 mb-6">
        <h3 className="font-semibold text-gray-800 mb-1">Database Utilities</h3>
        <p className="text-sm text-gray-500 mb-4">Export invoices for a date range to CSV (legacy Export Invoices).</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">From</span>
            <input type="date" value={expStart} onChange={e => setExpStart(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">To</span>
            <input type="date" value={expEnd} onChange={e => setExpEnd(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" />
          </label>
          <Button onClick={exportInvoices} disabled={exporting}><Download size={16} /> {exporting ? 'Exporting…' : 'Export Invoices CSV'}</Button>
          <Button variant="secondary" onClick={() => setQbExportOpen(true)}><Download size={16} /> QuickBooks Export</Button>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-4">Recent Activity Log</h3>
        <DataTable
          rows={activity}
          emptyMessage="No activity recorded"
          columns={[
            { key: 'activity_date', header: 'Date', render: a => `${a.activity_date ?? ''} ${a.activity_time ?? ''}`.trim() || '—' },
            { key: 'form', header: 'Form', render: a => a.form || '—' },
            { key: 'entry_type', header: 'Type', render: a => a.entry_type || '—' },
            { key: 'entry', header: 'Entry', render: a => <span className="line-clamp-1">{a.entry || '—'}</span> },
            { key: 'computer_name', header: 'Computer', render: a => a.computer_name || '—' },
          ]}
        />
      </Card>

      {editing && (
        <ClerkEditor
          clerk={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}

      {qbExportOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setQbExportOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <QuickBooksExport onClose={() => setQbExportOpen(false)} />
          </div>
        </div>
      )}
    </div>
  )
}

function ClerkEditor({ clerk, onClose, onSaved }: { clerk: ClerkAdmin | null; onClose: () => void; onSaved: () => void }) {
  const isNew = clerk === null
  const [username, setUsername] = useState(clerk?.username ?? '')
  const [email, setEmail] = useState(clerk?.email ?? '')
  const [role, setRole] = useState(clerk?.role ?? 'cashier')
  const [active, setActive] = useState(clerk?.is_active ?? true)
  const [pin, setPin] = useState('')
  const [perms, setPerms] = useState<Record<string, boolean | number>>(() => ({ ...(clerk?.perms ?? {}) }))
  const [saving, setSaving] = useState(false)

  const setFlag = (key: string, val: boolean) => setPerms(p => ({ ...p, [key]: val }))
  const setAmount = (key: string, val: string) => setPerms(p => ({ ...p, [key]: Number(val) || 0 }))

  const groups = [...new Set(CLERK_PERMISSIONS.map(p => p.group))]

  const save = async () => {
    if (!username.trim()) { toast.error('Name is required'); return }
    if (isNew && !pin.trim()) { toast.error('A PIN is required for a new clerk'); return }
    setSaving(true)
    try {
      const amountPerms: Record<string, number> = {}
      for (const a of CLERK_AMOUNT_PERMISSIONS) amountPerms[a.key] = Number(perms[a.key]) || 0
      const flagPerms: Record<string, boolean> = {}
      for (const p of CLERK_PERMISSIONS) flagPerms[p.key] = perms[p.key] === true
      const allPerms = { ...flagPerms, ...amountPerms }

      if (isNew) {
        await manageClerk('create', { username: username.trim(), email: email.trim() || undefined, role, pin: pin.trim(), is_active: active, permissions: allPerms })
        toast.success('Clerk created')
      } else if (clerk?.user_id) {
        await manageClerk('update', { user_id: clerk.user_id, username: username.trim(), email: email.trim(), role })
        await updateClerkPermissions(clerk.user_id, allPerms)
        if (active !== clerk.is_active) await manageClerk('set_active', { user_id: clerk.user_id, is_active: active })
        if (pin.trim()) await manageClerk('reset_pin', { user_id: clerk.user_id, pin: pin.trim() })
        toast.success('Clerk updated')
      } else {
        toast.error('This clerk has no linked login and cannot be edited here.')
        setSaving(false); return
      }
      onSaved()
    } catch (e) { toast.error((e as Error).message); setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-800">{isNew ? 'New Clerk' : `Edit ${clerk?.username}`}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Name</span>
              <input className="w-full border border-gray-300 rounded-lg px-3 py-2" value={username} onChange={e => setUsername(e.target.value)} />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Login email {isNew && <span className="text-gray-400">(auto from name if blank)</span>}</span>
              <input className="w-full border border-gray-300 rounded-lg px-3 py-2" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@thomsons.local" />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Role</span>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 capitalize" value={role} onChange={e => setRole(e.target.value)}>
                {ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
              </select>
            </label>
            <label className="text-sm flex items-end gap-2 pb-2">
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="h-4 w-4" />
              <span className="text-gray-700">Active (can sign in)</span>
            </label>
          </div>

          <label className="text-sm block">
            <span className="block text-gray-600 mb-1 flex items-center gap-1"><KeyRound size={14} /> {isNew ? 'PIN' : 'Reset PIN (leave blank to keep)'}</span>
            <input type="password" autoComplete="new-password" className="w-full sm:w-1/2 border border-gray-300 rounded-lg px-3 py-2" value={pin} onChange={e => setPin(e.target.value)} placeholder="••••" />
          </label>

          <div>
            <h4 className="font-medium text-gray-800 mb-2">Permissions</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {groups.map(g => (
                <div key={g}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{g}</p>
                  <div className="space-y-1">
                    {CLERK_PERMISSIONS.filter(p => p.group === g).map(p => (
                      <label key={p.key} className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" className="h-4 w-4" checked={perms[p.key] === true} onChange={e => setFlag(p.key, e.target.checked)} />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Limits</p>
                <div className="space-y-2">
                  {CLERK_AMOUNT_PERMISSIONS.map(a => (
                    <label key={a.key} className="flex items-center justify-between gap-2 text-sm text-gray-700">
                      <span>{a.label}</span>
                      <input type="number" min={0} step="0.01" className="w-28 border border-gray-300 rounded-lg px-2 py-1 text-right"
                        value={Number(perms[a.key]) || 0} onChange={e => setAmount(a.key, e.target.value)} />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : (isNew ? 'Create Clerk' : 'Save Changes')}</Button>
        </div>
      </div>
    </div>
  )
}
